import { AppButton, LoadingSpinner, FigmaTypography } from "@libs/zapi-ui/custom";
import { IQuestion } from "./IQuestion";
import { SparkleIcon } from "@libs/zapi-ui/assets";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getIntelligenceQuestions } from "../services/getIntelligenceQuestions";
import { toast } from "sonner";
import { useEffect, useState, useRef } from "react";
import { postGenerateQuestions } from "../services/postGenerateQuestions";
import { TIntelligenceQuestion } from "../model/types";
import { postDeepResearch } from "../services/postDeepResearch";
import { IntelligenceQuestionsHeader } from "./_IntelligenceQuestionsHeader";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { CACHE_TIME } from "@/zapi-frontend/shared/const/consts";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";

export function IntelligenceQuestions({ projectId }: { projectId: string }) {
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const { fetchSelectedProject } = useProjectStore();
  const { activeChapter, initializeProjectChapters } = useChapterStore();
  const { currentOrganizationId } = useOrganizationStore();
  const queryClient = useQueryClient();

  const activeChapterRef = useRef(activeChapter);
  activeChapterRef.current = activeChapter;

  const chapterId = activeChapter?.id?.toString();
  const [questions, setQuestions] = useState<TIntelligenceQuestion[]>([]);
  const { data: questionsData, isLoading: isLoadingQuestions } = useQuery({
    queryKey: ["intelligence-questions", projectId, chapterId, currentOrganizationId],
    queryFn: async () => {
      try {
        if (!chapterId) return [];

        const questions = await getIntelligenceQuestions({ projectId, chapterId });
        return questions;
      } catch (error) {
        console.error(error);
        toast.error("Failed to fetch intelligence questions");
        return [];
      }
    },
    enabled: !!chapterId && !!projectId,
    staleTime: CACHE_TIME,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (questionsData) setQuestions(questionsData);
  }, [questionsData]);

  const [generatingQuestions, setGeneratingQuestions] = useState<Set<string>>(new Set());
  const [researchingQuestions, setResearchingQuestions] = useState<Set<number>>(new Set());

  const handleGenerateQuestions = async () => {
    try {
      if (!chapterId) return;

      const currentChapterId = chapterId;
      setQuestions([]);
      setGeneratingQuestions((prev) => {
        const next = new Set(prev);
        next.add(currentChapterId);
        return next;
      });

      const questions = await postGenerateQuestions({
        projectId: Number(projectId),
        chapterId: Number(currentChapterId)
      });

      const currentActiveChapterId = activeChapterRef.current?.id?.toString();
      const isStillSameChapter = currentActiveChapterId === currentChapterId;

      if (isStillSameChapter) setQuestions((prev) => [...prev, ...questions]);
      queryClient.invalidateQueries({ queryKey: ["intelligence-questions", projectId, currentChapterId] });
      toast.success("Intelligence questions generated successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate intelligence questions");
    } finally {
      if (chapterId) {
        setGeneratingQuestions((prev) => {
          const next = new Set(prev);
          next.delete(chapterId);
          return next;
        });
      }
    }
  };

  const handleResearchClick = async (question: TIntelligenceQuestion) => {
    try {
      setResearchingQuestions((prev) => {
        const next = new Set(prev);
        next.add(question.id);
        return next;
      });
      const { message, newChapterDetails } = await postDeepResearch(question.id);
      const response = await fetchSelectedProject(projectId);
      if (!response) return;
      initializeProjectChapters(response);
      const text = message || "Research completed";
      toast.success(text);
      queryClient.invalidateQueries({ queryKey: ["intelligence-questions", projectId, chapterId] });
      setQuestions((prev) =>
        prev.map((q) =>
          q.id === question.id
            ? {
                ...q,
                createdProjectChapter: {
                  id: newChapterDetails.projectChapterId,
                  name: newChapterDetails.name
                }
              }
            : q
        )
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to research");
    } finally {
      setResearchingQuestions((prev) => {
        const next = new Set(prev);
        next.delete(question.id);
        return next;
      });
    }
  };

  const showGenerateMore = questions.every((q) => !!q.createdProjectChapter);
  const isGenerating = generatingQuestions.has(chapterId ?? "");

  return (
    <div className="h-full flex flex-col flex-grow overflow-y-auto" data-testid="intelligence-questions-root">
      <IntelligenceQuestionsHeader />

      {isLoadingQuestions && (
        <div className="h-full flex flex-col p-2 gap-2 items-center justify-center bg-innoveraZinc-100 rounded-b-[8px]">
          <LoadingSpinner />
          <FigmaTypography variant="body-3" className="text-zinc-500 text-center font-light">
            Loading intelligence questions...
          </FigmaTypography>
        </div>
      )}

      {questions.length === 0 && !isLoadingQuestions && (
        <div className="h-full flex flex-col p-2 gap-2 items-center justify-center bg-innoveraZinc-100 rounded-b-[8px]">
          <FigmaTypography variant="body-3" className="text-zinc-500 text-center font-light">
            No questions. {hasEdit ? "Generate to start" : ""}
          </FigmaTypography>
          {hasEdit && (
            <AppButton
              startAdornment={<SparkleIcon className="w-4 h-4" />}
              size="sm"
              disabled={isGenerating}
              onClick={handleGenerateQuestions}
              data-testid="generate-questions-button"
            >
              {isGenerating ? "Generating..." : "Generate"}
            </AppButton>
          )}
        </div>
      )}

      {questions.length > 0 && !isLoadingQuestions && (
        <div
          className="h-full flex flex-col gap-2 p-1 bg-innoveraZinc-100 overflow-y-auto rounded-b-[8px]"
          data-testid="intelligence-questions-list"
        >
          {questions.map((question) => (
            <IQuestion
              key={question.id}
              question={question}
              onResearchClick={handleResearchClick}
              isLoading={researchingQuestions.has(question.id)}
            />
          ))}
          {hasEdit && showGenerateMore && (
            <AppButton
              startAdornment={<SparkleIcon className="w-4 h-4" />}
              onClick={handleGenerateQuestions}
              isLoading={generatingQuestions.has(chapterId ?? "")}
              loadingText="Generating..."
            >
              Generate Questions
            </AppButton>
          )}
        </div>
      )}
    </div>
  );
}
